import React from "react";
import { themeTextStyles } from "../../styles";
import { ThemeColor } from "../../props";
import { cn } from "../../styles";

export interface CircularProgressProps {
  /**
   * Additional class name(s) to apply to the SVG element.
   * Use width to control size, overrides `size` property.
   */
  className?: string;

  /**
   * Progress value as a percentage (0–100) or `"busy"` for indeterminate spinner mode.
   * @default "busy"
   */
  progress?: number | "busy";

  /**
   * Color used for the stroke.
   * @default ThemeColor.none
   */
  color?: ThemeColor;

  /**
   * Whether the progress stroke ends are rounded.
   * @default true
   */
  rounded?: boolean;

  /**
   * Thickness of the stroke in percentage. 100 equals fully filled
   * @default 33
   */
  thickness?: number;

  /**
   * Width and height of the circle in pixels.
   * @default 32
   */
  size?: number;

  /**
   * Duration (in seconds) of one full spin in busy mode.
   * Ignored when a numeric progress value is given.
   * @default 1
   */
  spinDuration?: number;
}

/**
 * A circular progress indicator rendering SVG, that can show determinate (numeric) or indeterminate ("busy") states.
 */
export const CircularProgress = ({
  className = "",
  progress = "busy",
  color = ThemeColor.none,
  rounded = true,
  thickness = 33,
  size = 64,
  spinDuration = 1,
}: CircularProgressProps) => {
  const radius = 50 - thickness / 4;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset =
    circumference * (1 - (typeof progress === "number" ? progress : 0) / 100);

  const busy = typeof progress !== "number";

  return (
    <svg
      width={size}
      height={size}
      style={{
        transformOrigin: "50% 50%",
        animation: busy
          ? `circular-progress-rotate ${spinDuration}s linear infinite`
          : undefined,
      }}
      className={cn(themeTextStyles[color], className)}
      viewBox="0 0 100 100"
    >
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={thickness / 2}
        className="opacity-25"
      />
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={thickness / 2}
        strokeLinecap={rounded ? "round" : "butt"}
        {...(busy
          ? {
              style: {
                animation: `circular-progress-dash ${spinDuration}s ease-in-out infinite`,
              },
            }
          : {
              strokeDasharray,
              strokeDashoffset,
              transform: "rotate(-90 50 50)",
            })}
      />
    </svg>
  );
};
